import React, { useState, useEffect } from 'react';
import { useAuth } from '../../services/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { db } from '../../services/firebase';
import { FIRESTORE_COLLECTIONS } from '@financial-portfolio/shared/types';
import { doc, setDoc, getDoc, collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { AvatarSelectionModal } from './AvatarSelectionModal';
import { AccountSharingModal } from './AccountSharingModal';
import { ipTrackingService, UserIPInfo } from '../../services/IPTrackingService';
import { useScrollLock } from '../../hooks/useScrollLock';
import { CountrySelector } from '../CountrySelector';
import { useCountry } from '../../services/CountryContext';

interface ActivityLog {
  id: string;
  type: string;
  description: string;
  timestamp: Date;
}

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: TabType;
}

type TabType = 'general' | 'security' | 'notifications' | 'activity';

export const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose, initialTab = 'general' }) => {
  // Prevent background scrolling when modal is open
  useScrollLock(isOpen);

  const { user, isAdmin, logout } = useAuth();
  const { theme: currentTheme, setTheme: setGlobalTheme, resolvedTheme } = useTheme();
  const { countryCode, setCountryCode } = useCountry();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [notifications, setNotifications] = useState({
    portfolio: true,
    market: true,
    property: true,
    ai: false,
    // Email preferences - Reports & News
    emailMonthlyReview: true,
    emailMarketNews: true,
    // Email preferences - Property Alerts
    emailRentIncrease: true,
    emailLeaseExpiry: true,
    emailInsuranceRenewal: true,
    emailComplianceCerts: true,  // Fire cert, pool cert, etc.
    // Email preferences - Financial Alerts
    emailCouncilRates: true,
    emailLoanReview: true,
    emailLandTax: true,
  });
  const [saving, setSaving] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string>('');
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [userIPInfo, setUserIPInfo] = useState<UserIPInfo | null>(null);
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [passwordMessage, setPasswordMessage] = useState({ text: '', type: '' });
  const [showSharingModal, setShowSharingModal] = useState(false);

  const userName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const userEmail = user?.email || '';
  const defaultPhotoUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&size=128&background=4F46E5&color=fff`;
  const userPhotoUrl = currentPhotoUrl || user?.photoURL || defaultPhotoUrl;

  // Update photo URL when user changes
  useEffect(() => {
    if (user?.photoURL) {
      setCurrentPhotoUrl(user.photoURL);
    }
  }, [user]);

  // Get auth provider
  const getAuthProvider = () => {
    if (user?.providerData && user.providerData.length > 0) {
      const provider = user.providerData[0].providerId;
      if (provider === 'google.com') return 'Google';
      if (provider === 'password') return 'Email/Password';
      return provider;
    }
    return 'Unknown';
  };

  const isGoogleUser = getAuthProvider() === 'Google';

  // Update active tab when modal opens or initialTab changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Load user preferences when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadUserPreferences();
      if (activeTab === 'activity') {
        loadActivityLog();
      }
      if (activeTab === 'security') {
        loadUserIPInfo();
      }
    }
  }, [isOpen, user, activeTab]);

  // Theme is now managed by ThemeContext - no need to initialize here

  // Load user preferences from Firestore
  const loadUserPreferences = async () => {
    if (!user || !user.email) return;

    try {
      const userDocRef = doc(db, FIRESTORE_COLLECTIONS.USERS, user.email.toLowerCase());
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();

        // Load theme preference from Firestore and apply via ThemeContext
        const firestoreTheme = data?.["preferences"]?.["theme"];
        if (firestoreTheme) {
          setGlobalTheme(firestoreTheme);
        }

        // Load notification preferences
        if (data?.["notificationPreferences"]) {
          setNotifications(data["notificationPreferences"]);
        }
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  // Handle theme change - uses ThemeContext for global state
  const handleThemeChange = async (newTheme: 'light' | 'dark' | 'system') => {
    // Update global theme via context (this also saves to localStorage and Firestore)
    setGlobalTheme(newTheme);
  };

  // Format dates
  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSignOut = async () => {
    onClose();
    await logout();
    // Navigation handled by logout function
  };

  const handleAdminPanel = () => {
    onClose();
    window.location.href = '/admin';
  };

  const handleSaveNotifications = async () => {
    if (!user || !user.email) return;

    setSaving(true);
    try {
      const userDocRef = doc(db, 'users', user.email.toLowerCase());
      await setDoc(userDocRef, {
        notificationPreferences: notifications,
        updatedAt: new Date()
      }, { merge: true });

      alert('Notification preferences saved successfully!');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Security: File size limit - 200KB
    const MAX_FILE_SIZE = 200 * 1024; // 200KB in bytes
    if (file.size > MAX_FILE_SIZE) {
      alert(`File size must be less than 200KB. Your file is ${(file.size / 1024).toFixed(2)}KB`);
      event.target.value = ''; // Clear the input
      return;
    }

    // Security: Validate file type with multiple checks
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

    // Check MIME type
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert('Only JPEG, PNG, and WebP images are allowed');
      event.target.value = '';
      return;
    }

    // Check file extension (prevents MIME type spoofing)
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      alert('Invalid file extension. Only .jpg, .jpeg, .png, and .webp are allowed');
      event.target.value = '';
      return;
    }

    // Security: Sanitize filename - remove special characters
    const sanitizedName = file.name
      .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
      .replace(/_{2,}/g, '_') // Replace multiple underscores with single
      .substring(0, 100); // Limit filename length

    // Security: Verify it's actually an image by reading file header
    try {
      const buffer = await file.slice(0, 4).arrayBuffer();
      const bytes = new Uint8Array(buffer);
      const isValidImage =
        // JPEG: FF D8 FF
        (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) ||
        // PNG: 89 50 4E 47
        (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) ||
        // WebP: 52 49 46 46 (RIFF)
        (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46);

      if (!isValidImage) {
        alert('File appears to be corrupted or is not a valid image');
        event.target.value = '';
        return;
      }
    } catch (error) {
      console.error('Error validating file:', error);
      alert('Error validating file. Please try again.');
      event.target.value = '';
      return;
    }

    setSaving(true);
    try {
      const storage = getStorage();

      // Security: Use timestamp and sanitized filename to prevent path traversal
      const timestamp = Date.now();
      const secureFileName = `${timestamp}_${sanitizedName}`;

      // Security: Store in user-specific folder (enforced by Storage Rules)
      const photoRef = ref(storage, `profile-photos/${user.uid}/${secureFileName}`);

      // Security: Set metadata with content type validation
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: user.uid,
          uploadedAt: new Date().toISOString(),
          originalName: file.name.substring(0, 100)
        }
      };

      // Upload file with metadata
      await uploadBytes(photoRef, file, metadata);

      // Get secure download URL with token
      const downloadURL = await getDownloadURL(photoRef);

      // Update Firebase Auth profile
      await updateProfile(user, {
        photoURL: downloadURL
      });

      // Update Firestore with security metadata
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        photoURL: downloadURL,
        photoMetadata: {
          fileName: secureFileName,
          fileSize: file.size,
          contentType: file.type,
          uploadedAt: Timestamp.now()
        },
        updatedAt: Timestamp.now()
      }, { merge: true });

      // Update local state
      setCurrentPhotoUrl(downloadURL);

      // Clear input for security
      event.target.value = '';

      alert('Profile photo updated successfully!');
    } catch (error) {
      console.error('Error uploading photo:', error);

      // Security: Don't expose internal error details to user
      if (error instanceof Error && error.message.includes('unauthorized')) {
        alert('You do not have permission to upload photos. Please contact support.');
      } else {
        alert('Failed to upload photo. Please try again.');
      }

      event.target.value = '';
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelected = (url: string) => {
    setCurrentPhotoUrl(url);
  };

  // Load user IP information
  const loadUserIPInfo = async () => {
    if (!user || !user.email) return;

    try {
      // Use Cloud Function to get user IP info (handles permissions)
      const result = await ipTrackingService.getUserIPHistory(user.email, 1);

      if (result && result.userInfo) {
        setUserIPInfo({
          email: result.userInfo.email,
          lastLoginIP: result.userInfo.lastLoginIP || 'N/A',
          lastLoginAt: result.userInfo.lastLoginAt || null,
          firstLoginIP: result.userInfo.firstLoginIP || 'N/A',
          firstLoginAt: result.userInfo.firstLoginAt || null,
          totalLogins: result.userInfo.totalLogins || 0
        });
      }
    } catch (error) {
      console.error('Error loading IP info:', error);
    }
  };

  // Load activity log
  const loadActivityLog = async () => {
    if (!user || !user.email) return;

    setLoadingActivity(true);
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const activityRef = collection(db, 'users', user.email.toLowerCase(), 'activity');
      const q = query(
        activityRef,
        where('timestamp', '>=', Timestamp.fromDate(sevenDaysAgo)),
        orderBy('timestamp', 'desc'),
        limit(20)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setActivityLogs([]);
        setLoadingActivity(false);
        return;
      }

      const activities: ActivityLog[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          type: data['type'] || 'default',
          description: data['description'] || 'Activity',
          timestamp: data['timestamp']?.toDate() || new Date()
        };
      });

      setActivityLogs(activities);
    } catch (error) {
      console.error('Error loading activity log:', error);
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Format activity time
  const formatActivityTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  };

  // Get activity icon
  const getActivityIcon = (type: string) => {
    const icons: { [key: string]: React.JSX.Element } = {
      'signin': <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clipRule="evenodd" /></svg>,
      'property_added': <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>,
      'profile_photo_updated': <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" /></svg>,
      'notifications_updated': <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" /></svg>,
      'default': <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
    };
    return icons[type] || icons['default'];
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !user.email) {
      setPasswordMessage({ text: 'User not authenticated', type: 'error' });
      return;
    }

    if (passwordData.new !== passwordData.confirm) {
      setPasswordMessage({ text: 'New passwords do not match', type: 'error' });
      return;
    }

    if (passwordData.new.length < 6) {
      setPasswordMessage({ text: 'Password must be at least 6 characters', type: 'error' });
      return;
    }

    setSaving(true);
    setPasswordMessage({ text: '', type: '' });

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, passwordData.current);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, passwordData.new);

      // Clear form
      setPasswordData({ current: '', new: '', confirm: '' });

      setPasswordMessage({ text: 'Password updated successfully!', type: 'success' });

      setTimeout(() => setPasswordMessage({ text: '', type: '' }), 3000);
    } catch (error: any) {
      console.error('Error changing password:', error);
      let errorMessage = 'Failed to update password. ';

      if (error.code === 'auth/wrong-password') {
        errorMessage += 'Current password is incorrect.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage += 'New password is too weak.';
      } else {
        errorMessage += error.message;
      }

      setPasswordMessage({ text: errorMessage, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-3xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-semibold text-gray-900">Profile & Settings</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close profile modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Profile Info Header */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="relative">
              <img
                src={userPhotoUrl}
                alt="Profile picture"
                className="w-20 h-20 rounded-full border-2 border-gray-300"
              />
              <button
                onClick={() => document.getElementById('photo-upload-input')?.click()}
                className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-full shadow-lg"
                aria-label="Change photo"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-semibold text-gray-900">{userName}</h4>
              <p className="text-sm text-gray-600">{userEmail}</p>
              {isAdmin && (
                <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 mt-1">
                  Admin
                </span>
              )}
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => setShowAvatarModal(true)}
                  className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Choose Avatar
                </button>
                <button
                  onClick={() => document.getElementById('photo-upload-input')?.click()}
                  className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Upload Photo
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          id="photo-upload-input"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          className="hidden"
          onChange={handlePhotoUpload}
          aria-label="Upload profile photo (JPEG, PNG, WebP only, max 200KB)"
        />

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-4" aria-label="Tabs">
              {(['general', 'security', 'notifications', 'activity'] as TabType[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-4">
              {/* Theme Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Theme</h5>
                  <p className="text-sm text-gray-600">Choose your preferred color scheme</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleThemeChange('light')}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${currentTheme === 'light' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    title="Light Theme"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <span className="text-[10px] uppercase font-bold tracking-wider">Light</span>
                  </button>
                  <button
                    onClick={() => handleThemeChange('dark')}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${currentTheme === 'dark' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    title="Dark Theme"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                    <span className="text-[10px] uppercase font-bold tracking-wider">Dark</span>
                  </button>
                  <button
                    onClick={() => handleThemeChange('system')}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${currentTheme === 'system' ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    title="System Default"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 21h6l-.75-4M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-[10px] uppercase font-bold tracking-wider">Auto</span>
                  </button>
                </div>
              </div>

              {/* Country Selection */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Region & Currency</h5>
                  <p className="text-sm text-gray-600">Select your country for local property analysis</p>
                </div>
                <div className="w-48">
                  <CountrySelector value={countryCode} onChange={setCountryCode} />
                </div>
              </div>

              {/* Admin Panel Link */}
              {isAdmin && (
                <button
                  onClick={handleAdminPanel}
                  className="w-full px-4 py-3 text-left bg-red-50 hover:bg-red-100 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <svg className="h-5 w-5 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.40-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65Z" />
                  </svg>
                  <div className="flex-1">
                    <div className="font-medium text-red-900">Admin Dashboard</div>
                    <div className="text-xs text-red-700">Manage users and settings</div>
                  </div>
                </button>
              )}

              {/* Share Access - Only for account owners */}
              <button
                onClick={() => setShowSharingModal(true)}
                className="w-full px-4 py-3 text-left bg-purple-50 hover:bg-purple-100 rounded-lg flex items-center gap-3 transition-colors"
              >
                <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5.5A3.5 3.5 0 0 1 15.5 9a3.5 3.5 0 0 1-3.5 3.5A3.5 3.5 0 0 1 8.5 9 3.5 3.5 0 0 1 12 5.5M5 8c.56 0 1.08.15 1.53.42-.15 1.43.27 2.85 1.13 3.96C7.16 13.34 6.16 14 5 14a3 3 0 0 1-3-3 3 3 0 0 1 3-3m14 0a3 3 0 0 1 3 3 3 3 0 0 1-3 3c-1.16 0-2.16-.66-2.66-1.62a5.536 5.536 0 0 0 1.13-3.96c.45-.27.97-.42 1.53-.42M5.5 18.25c0-2.07 2.91-3.75 6.5-3.75s6.5 1.68 6.5 3.75V20h-13v-1.75M0 20v-1.5c0-1.39 1.89-2.56 4.45-2.9-.59.68-.95 1.62-.95 2.65V20H0m24 0h-3.5v-1.75c0-1.03-.36-1.97-.95-2.65 2.56.34 4.45 1.51 4.45 2.9V20z" />
                </svg>
                <div className="flex-1">
                  <div className="font-medium text-purple-900">Share Portfolio Access</div>
                  <div className="text-xs text-purple-700">Invite others to view your portfolio</div>
                </div>
              </button>

              {/* Sign Out */}
              <button
                onClick={handleSignOut}
                className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center gap-3 transition-colors"
              >
                <svg className="h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z" />
                </svg>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Sign Out</div>
                  <div className="text-xs text-gray-600">Sign out of your account</div>
                </div>
              </button>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-4">
              {/* Auth Provider Info */}
              {isGoogleUser ? (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h5 className="font-medium text-blue-900">Google Account</h5>
                      <p className="text-sm text-blue-700 mt-1">
                        You're signed in with Google. Password management is handled through your Google account.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-3">Change Password</h5>
                  <form onSubmit={handlePasswordChange} className="space-y-3">
                    <div>
                      <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <input
                        id="current-password"
                        type="password"
                        value={passwordData.current}
                        onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <input
                        id="new-password"
                        type="password"
                        value={passwordData.new}
                        onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        minLength={6}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                    </div>
                    <div>
                      <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <input
                        id="confirm-password"
                        type="password"
                        value={passwordData.confirm}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    {passwordMessage.text && (
                      <div className={`text-sm ${passwordMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {passwordMessage.text}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={saving}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>
              )}

              {/* Account Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-3">Account Information</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Account Created</span>
                    <span className="text-gray-900 font-medium">
                      {formatDate(user?.metadata?.creationTime || null)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Sign In</span>
                    <span className="text-gray-900 font-medium">
                      {formatDate(user?.metadata?.lastSignInTime || null)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auth Provider</span>
                    <span className="text-gray-900 font-medium">{getAuthProvider()}</span>
                  </div>
                  {userIPInfo && (
                    <>
                      <div className="border-t border-gray-200 my-3"></div>
                      <div className="flex justify-between items-start">
                        <span className="text-gray-600">Last Login IP</span>
                        <span className="text-gray-900 font-mono text-xs">
                          {userIPInfo.lastLoginIP}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Logins</span>
                        <span className="text-gray-900 font-medium">
                          {userIPInfo.totalLogins}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">Manage your email notification preferences</p>

              {/* Portfolio Updates */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Portfolio Updates</h5>
                  <p className="text-sm text-gray-600">Weekly summary of your portfolio performance</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.portfolio}
                    onChange={(e) => setNotifications({ ...notifications, portfolio: e.target.checked })}
                    className="sr-only peer"
                    aria-label="Toggle portfolio updates notifications"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>

              {/* Market Insights */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Market Insights</h5>
                  <p className="text-sm text-gray-600">RBA rate changes and market updates</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.market}
                    onChange={(e) => setNotifications({ ...notifications, market: e.target.checked })}
                    className="sr-only peer"
                    aria-label="Toggle market insights notifications"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>

              {/* Property Alerts */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Property Alerts</h5>
                  <p className="text-sm text-gray-600">Rent reminders and property value changes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.property}
                    onChange={(e) => setNotifications({ ...notifications, property: e.target.checked })}
                    className="sr-only peer"
                    aria-label="Toggle property alerts notifications"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>

              {/* AI Insights */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">AI Insights</h5>
                  <p className="text-sm text-gray-600">Personalized financial advice notifications</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.ai}
                    onChange={(e) => setNotifications({ ...notifications, ai: e.target.checked })}
                    className="sr-only peer"
                    aria-label="Toggle AI insights notifications"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>

              {/* Email Preferences Section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Preferences
                </h4>
                <p className="text-sm text-gray-500 mb-4">Choose what emails you'd like to receive</p>

                {/* Reports & News Section */}
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Reports & News
                  </h5>

                  {/* Monthly Portfolio Review */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-2">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Monthly Portfolio Review</h6>
                      <p className="text-xs text-gray-600">Summary on the 1st of each month</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailMonthlyReview}
                        onChange={(e) => setNotifications({ ...notifications, emailMonthlyReview: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle monthly portfolio review emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                    </label>
                  </div>

                  {/* Market News */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Market News Digest</h6>
                      <p className="text-xs text-gray-600">Weekly RBA & property market updates</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailMarketNews}
                        onChange={(e) => setNotifications({ ...notifications, emailMarketNews: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle market news emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                    </label>
                  </div>
                </div>

                {/* Property Alerts Section */}
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Property Alerts
                  </h5>

                  {/* Rent Increase */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg mb-2">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Rent Increase Reminders</h6>
                      <p className="text-xs text-gray-600">90, 60, 30 days before rent increase date</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailRentIncrease}
                        onChange={(e) => setNotifications({ ...notifications, emailRentIncrease: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle rent increase emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
                    </label>
                  </div>

                  {/* Lease Expiry */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg mb-2">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Lease Expiry Alerts</h6>
                      <p className="text-xs text-gray-600">Reminders before lease ends</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailLeaseExpiry}
                        onChange={(e) => setNotifications({ ...notifications, emailLeaseExpiry: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle lease expiry emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
                    </label>
                  </div>

                  {/* Insurance Renewal */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg mb-2">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Insurance Renewal</h6>
                      <p className="text-xs text-gray-600">Building & landlord insurance reminders</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailInsuranceRenewal}
                        onChange={(e) => setNotifications({ ...notifications, emailInsuranceRenewal: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle insurance renewal emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
                    </label>
                  </div>

                  {/* Compliance Certificates */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Compliance Certificates</h6>
                      <p className="text-xs text-gray-600">Fire safety, pool cert, smoke alarms</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailComplianceCerts}
                        onChange={(e) => setNotifications({ ...notifications, emailComplianceCerts: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle compliance certificate emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
                    </label>
                  </div>
                </div>

                {/* Financial Alerts Section */}
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Financial Alerts
                  </h5>

                  {/* Council Rates */}
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg mb-2">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Council & Water Rates</h6>
                      <p className="text-xs text-gray-600">Payment due date reminders</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailCouncilRates}
                        onChange={(e) => setNotifications({ ...notifications, emailCouncilRates: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle council rates emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600" />
                    </label>
                  </div>

                  {/* Loan Review */}
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg mb-2">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Loan Review Dates</h6>
                      <p className="text-xs text-gray-600">Fixed rate expiry, IO period end</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailLoanReview}
                        onChange={(e) => setNotifications({ ...notifications, emailLoanReview: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle loan review emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600" />
                    </label>
                  </div>

                  {/* Land Tax */}
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div className="flex-1">
                      <h6 className="font-medium text-gray-900 text-sm">Land Tax Reminders</h6>
                      <p className="text-xs text-gray-600">Annual land tax assessment alerts</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.emailLandTax}
                        onChange={(e) => setNotifications({ ...notifications, emailLandTax: e.target.checked })}
                        className="sr-only peer"
                        aria-label="Toggle land tax emails"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600" />
                    </label>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSaveNotifications}
                disabled={saving}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Notification Preferences'}
              </button>
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">Your recent activity (last 30 days)</p>
              <div className="space-y-2">
                {loadingActivity ? (
                  <p className="text-sm text-gray-500 text-center py-8">Loading activity...</p>
                ) : activityLogs.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No recent activity</p>
                ) : (
                  activityLogs.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-gray-400 mt-0.5">{getActivityIcon(activity.type)}</div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{activity.description}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{formatActivityTime(activity.timestamp)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* App Version Info */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">CanvasEstate v1.0</p>
        </div>
      </div>

      {/* Avatar Selection Modal */}
      <AvatarSelectionModal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onAvatarSelected={handleAvatarSelected}
      />

      {/* Account Sharing Modal */}
      <AccountSharingModal
        isOpen={showSharingModal}
        onClose={() => setShowSharingModal(false)}
      />
    </div>
  );
};
